import { FC, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';

import {
    BackLink,
    Banner,
    BodyText,
    Button,
    ButtonGroup,
    Card,
    Form,
    Headline,
    Layout,
    Link as AnvilLink,
    Page,
    Stack,
} from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';

import { INTEGRATIONS_TEAM_EMAIL } from '../../common';
import { RequestNewAppPageStore } from '../stores/request-new-app-page.store';
import successIllustrationUrl from '../assets/success-illustration.svg';

export const RequestNewAppPage: FC = provide({ singletons: [RequestNewAppPageStore] })(
    observer(() => {
        const [store] = useDependencies(RequestNewAppPageStore);
        const history = useHistory();

        const handleCancel = useCallback(() => {
            history.push('/apps');
        }, [history]);

        const formState = store.form.$;

        if (store.submitted) {
            return (
                <Page header={<BackLink pageLabel="My Apps" onClick={handleCancel} />}>
                    <Stack
                        direction="column"
                        alignItems="center"
                        spacing={2}
                        className="p-y-6 ta-center m-x-auto"
                        style={{ maxWidth: 500 }}
                    >
                        <img src={successIllustrationUrl} width={300} height={216} alt="" />
                        <Headline el="h2" size="medium" className="m-b-4">
                            We’re reviewing this now!
                        </Headline>
                        <BodyText subdued>
                            Check your email for a confirmation message. We will contact you within
                            one business day to help you set up the app securely.
                        </BodyText>
                        <BodyText subdued>
                            If you don’t get an email in the next 10 minutes, reach out to
                            ServiceTitan at{' '}
                            <AnvilLink href={`mailto:${INTEGRATIONS_TEAM_EMAIL}`} primary>
                                {INTEGRATIONS_TEAM_EMAIL}
                            </AnvilLink>
                            .
                        </BodyText>
                    </Stack>
                </Page>
            );
        }

        return (
            <Page
                header={
                    <Stack direction="column" spacing={2}>
                        <Stack.Item>
                            <BackLink pageLabel="My Apps" onClick={handleCancel} />
                        </Stack.Item>
                        <Stack.Item>
                            <Headline el="h1" size="large">
                                Request Third-Party App Registration
                            </Headline>
                        </Stack.Item>
                    </Stack>
                }
                footer={
                    <Stack className="w-100" justifyContent="flex-end">
                        <ButtonGroup>
                            <Button onClick={handleCancel}>Cancel</Button>
                            <Button
                                primary
                                loading={store.saving}
                                onClick={async () => {
                                    if (!(await store.submit())) {
                                        document.querySelector('.Page__page')?.scrollTo(0, 0);
                                    }
                                }}
                            >
                                Submit
                            </Button>
                        </ButtonGroup>
                    </Stack>
                }
            >
                <Layout autoSections={false}>
                    {store.form.hasError && (
                        <Layout.Section>
                            <Banner
                                status="critical"
                                icon
                                title="Please correct the errors shown below."
                            />
                        </Layout.Section>
                    )}
                    <Layout.Section>
                        <BodyText>
                            Please fill out the app details below. When finished, click{' '}
                            <strong>Submit</strong>.
                        </BodyText>
                    </Layout.Section>
                    <Form className="w-100">
                        <Layout.Section>
                            <Card>
                                <Card.Section>
                                    <Headline el="h2" size="small">
                                        Third-Party Details
                                    </Headline>
                                    <Form.Input
                                        label="Organization"
                                        labelProps={{ required: true }}
                                        description="Name of the vendor building this app."
                                        maxLength={120}
                                        value={formState.organization.value}
                                        error={formState.organization.error}
                                        onChange={formState.organization.onChangeHandler}
                                    />
                                    <Form.Input
                                        label="Organization Homepage URL"
                                        labelProps={{ required: true }}
                                        type="url"
                                        placeholder="https://my-app.com"
                                        description="The organization's primary website address."
                                        maxLength={250}
                                        value={formState.homepageUrl.value}
                                        error={formState.homepageUrl.error}
                                        onChange={formState.homepageUrl.onChangeHandler}
                                    />
                                    <Form.Input
                                        label="Email Address"
                                        labelProps={{ required: true }}
                                        type="email"
                                        description="Contact address for the third party."
                                        maxLength={100}
                                        value={formState.emailAddress.value}
                                        error={formState.emailAddress.error}
                                        onChange={formState.emailAddress.onChangeHandler}
                                    />
                                    <Form.TextArea
                                        label="Description"
                                        labelProps={{ required: true }}
                                        description="What does the app do? Provide an example."
                                        maxLength={1000}
                                        showCounter
                                        defaultValue={formState.description.$ /* for the counter */}
                                        value={formState.description.value}
                                        error={formState.description.error}
                                        onChange={formState.description.onChangeHandler}
                                    />
                                </Card.Section>
                                <Card.Section>
                                    <BodyText>
                                        <strong>Why this matters:</strong> Third-party apps give the
                                        app creator access to your business’s data. Providing this
                                        information helps to make sure that this is done securely.
                                    </BodyText>
                                </Card.Section>
                            </Card>
                        </Layout.Section>
                    </Form>
                </Layout>
            </Page>
        );
    }),
);
RequestNewAppPage.displayName = 'RequestNewAppPage';
